/**
 * Time data. Entries live only in PocketBase — Todoist has nowhere to put them
 * and nothing is ever pushed back.
 *
 * Two collections:
 *  - `time_entries`  the log
 *  - `active_timer`  a singleton row for the one running timer, so a timer
 *                    started on the laptop is visible on the phone
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { applyRounding } from "@/lib/timeRollup";
import { roundingMinutes } from "@/lib/theme";
import { isTravelActivity, loadTravel, travelRate } from "@/lib/travel";

import { pb } from "@/lib/pocketbase";
import type { ActiveTimer, Client, Project, TimeEntry } from "@/lib/types";
import { projectRate } from "@/lib/billing";

const userId = (): string => (pb.authStore.model as { id?: string } | null)?.id ?? "";

/* -- Reads ------------------------------------------------------------------ */

export function useTimeEntries() {
  return useQuery({
    queryKey: ["time_entries"],
    queryFn: () =>
      pb.collection("time_entries").getFullList<TimeEntry>({
        sort: "-start",
        expand: "item,project,client",
      }),
  });
}

export function useClients() {
  return useQuery({
    queryKey: ["clients"],
    queryFn: () => pb.collection("clients").getFullList<Client>({ sort: "name" }),
  });
}

/**
 * The server-side running timer. Refetched on focus so returning to the tab
 * picks up a timer started (or stopped) somewhere else.
 */
export function useActiveTimer() {
  return useQuery({
    queryKey: ["active_timer"],
    refetchOnWindowFocus: true,
    queryFn: async (): Promise<ActiveTimer | null> => {
      const rows = await pb.collection("active_timer").getFullList<ActiveTimer>({ perPage: 1 });
      return rows[0] ?? null;
    },
  });
}

/* -- Rates ------------------------------------------------------------------ */

/**
 * The rate an entry should snapshot: the project's own override, else the
 * client default, else nothing. Resolved at creation and copied onto the entry,
 * so changing a client's rate next year never rewrites last year's invoices.
 *
 * Resolution order for the client: the `clientRef` relation if set, else a
 * name match against the free-text `client` field. The relation is exact; the
 * name match exists only so projects that predate it — or that came straight
 * from a Todoist sync, which has no concept of a client — still resolve a rate
 * until someone opens them and picks one.
 */
export function resolveClient(project: Project | undefined, clients: Client[]): Client | undefined {
  if (project?.clientRef) {
    const exact = clients.find((c) => c.id === project.clientRef);
    if (exact) return exact;
  }
  const key = (s: string | undefined) => (s ?? "").trim().toLowerCase();
  if (!key(project?.client)) return undefined;
  return clients.find((c) => key(c.name) === key(project?.client));
}

export function effectiveRate(
  project: Project | undefined,
  clients: Client[],
): { rate: number | null; currency: string; clientId: string | null } {
  const client = resolveClient(project, clients);
  return {
    rate: projectRate(project) ?? client?.defaultRate ?? null,
    currency: project?.currency || client?.currency || "USD",
    clientId: client?.id ?? null,
  };
}

/* -- Writes ----------------------------------------------------------------- */

export interface LogEntryInput {
  /** Absent for ad-hoc work — a call, an hour of email — that belongs to no
   *  project. `category` is what makes that time legible in a report. */
  projectId?: string;
  category?: string;
  itemId?: string;
  start: string;
  end: string;
  durationSeconds: number;
  note?: string;
  billable?: boolean;
  /** Admin / overhead. Callers set this, `billable` and `rate` together from
   *  lib/billing's kindFields(), so only Billable time ever carries a rate. */
  internal?: boolean;
  source?: TimeEntry["source"];
}

export function useClientMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["clients"] });
    // Rates are snapshotted onto entries at creation, so existing entries do
    // NOT change — but the projects list and every rate preview do.
    void qc.invalidateQueries({ queryKey: ["projects"] });
  };

  const saveClient = useMutation({
    mutationFn: (input: Partial<Client> & { id?: string }) =>
      input.id
        ? pb.collection("clients").update<Client>(input.id, input)
        : pb.collection("clients").create<Client>({ ...input, user: userId() } as unknown as Partial<Client>),
    onSuccess: invalidate,
  });

  const deleteClient = useMutation({
    mutationFn: (id: string) => pb.collection("clients").delete(id),
    onSuccess: invalidate,
  });

  return { saveClient, deleteClient };
}

export function useTimeMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["time_entries"] });
    void qc.invalidateQueries({ queryKey: ["active_timer"] });
  };

  const logEntry = useMutation({
    mutationFn: async (input: LogEntryInput & { rate: number | null; currency: string; clientId: string | null }) => {
      // Rounding lives here, not in the six places that log time. Every entry
      // — the capture bar, the quick-log bar, a stopped timer, a task row, the
      // phone — goes through this one call, so one snap covers all of them and
      // none of them can forget. The end moves with the duration so the entry
      // stays internally consistent.
      const durationSeconds = applyRounding(input.durationSeconds, roundingMinutes());
      const end = durationSeconds === Math.round(input.durationSeconds)
        ? input.end
        : new Date(new Date(input.start).getTime() + durationSeconds * 1000).toISOString();
      // The travel discount lives here for the same reason: picking the travel
      // activity anywhere time is logged bills it at the travel share.
      const travel = loadTravel();
      const isTravel = isTravelActivity(input.category, travel) && travel.pct < 100;
      const rate = input.billable === false || input.internal || input.rate === null
        ? null
        : isTravel ? travelRate(input.rate, travel.pct) : input.rate;
      return pb.collection("time_entries").create<TimeEntry>({
        user: userId(),
        project: input.projectId || null,
        item: input.itemId || null,
        client: input.clientId || null,
        category: input.category ?? "",
        start: input.start,
        end,
        durationSeconds,
        note: input.note ?? "",
        billable: input.billable ?? true,
        internal: Boolean(input.internal),
        rateSnapshot: rate,
        travelPct: isTravel && rate !== null ? travel.pct : null,
        currency: input.currency,
        source: input.source ?? "manual",
        orphaned: false,
      } as unknown as Partial<TimeEntry>);
    },
    onSuccess: invalidate,
  });

  const updateEntry = useMutation({
    mutationFn: (input: Partial<TimeEntry> & { id: string }) => {
      // Saving an entry against a project is how an orphan is reattached, on
      // the desk or the phone, so the flag goes with it. The sync only ever
      // sets it; nothing else clears it.
      const patch = input.project ? { ...input, orphaned: false } : input;
      // A corrected duration snaps too. Editing 12 minutes into an entry and
      // having it stay 12 when every new entry rounds to 15 is the kind of
      // inconsistency you only notice at invoice time.
      if (patch.durationSeconds === undefined) {
        return pb.collection("time_entries").update<TimeEntry>(patch.id, patch);
      }
      const durationSeconds = applyRounding(patch.durationSeconds, roundingMinutes());
      const end = patch.start
        ? new Date(new Date(patch.start).getTime() + durationSeconds * 1000).toISOString()
        : patch.end;
      return pb.collection("time_entries").update<TimeEntry>(patch.id, { ...patch, durationSeconds, end });
    },
    onSuccess: invalidate,
  });

  const deleteEntry = useMutation({
    mutationFn: (id: string) => pb.collection("time_entries").delete(id),
    onSuccess: invalidate,
  });

  /**
   * Stamp a set of entries as invoiced — or, with `invoicedAt: null`, take the
   * stamp back off. One request per entry: PocketBase has no bulk update over
   * the SDK, and a month is a few dozen rows at most. Sequential rather than
   * parallel so a failure part-way leaves a clean prefix done, not a scatter.
   */
  const setInvoiced = useMutation({
    mutationFn: async (input: { ids: string[]; invoicedAt: string | null; invoiceNumber?: string }) => {
      for (const id of input.ids) {
        await pb.collection("time_entries").update<TimeEntry>(id, {
          invoicedAt: input.invoicedAt ?? "",
          invoiceNumber: input.invoicedAt ? (input.invoiceNumber ?? "").trim() : "",
        } as unknown as Partial<TimeEntry>);
      }
    },
    onSuccess: invalidate,
  });

  /** Replaces any existing row: there is only ever one running timer. */
  const startTimer = useMutation({
    mutationFn: async (input: { startedAt: string; projectId?: string; itemId?: string; note?: string; billable?: boolean; internal?: boolean }) => {
      const existing = await pb.collection("active_timer").getFullList<ActiveTimer>({ perPage: 20 });
      await Promise.all(existing.map((r) => pb.collection("active_timer").delete(r.id)));
      return pb.collection("active_timer").create<ActiveTimer>({
        user: userId(),
        startedAt: input.startedAt,
        project: input.projectId || null,
        item: input.itemId || null,
        note: input.note ?? "",
        billable: input.billable ?? true,
        internal: Boolean(input.internal),
      } as unknown as Partial<ActiveTimer>);
    },
    onSuccess: invalidate,
  });

  /** Edits the running timer in place — a new project, task, kind or start. */
  const updateTimer = useMutation({
    mutationFn: async (patch: { startedAt?: string; projectId?: string; itemId?: string; note?: string; billable?: boolean; internal?: boolean }) => {
      const rows = await pb.collection("active_timer").getFullList<ActiveTimer>({ perPage: 1 });
      if (!rows[0]) return null;
      const body: Record<string, unknown> = {};
      if (patch.startedAt !== undefined) body.startedAt = patch.startedAt;
      if ("projectId" in patch) body.project = patch.projectId || null;
      if ("itemId" in patch) body.item = patch.itemId || null;
      if (patch.note !== undefined) body.note = patch.note;
      if (patch.billable !== undefined) body.billable = patch.billable;
      if (patch.internal !== undefined) body.internal = patch.internal;
      return pb.collection("active_timer").update<ActiveTimer>(rows[0].id, body);
    },
    onSuccess: invalidate,
  });

  const clearTimer = useMutation({
    mutationFn: async () => {
      const rows = await pb.collection("active_timer").getFullList<ActiveTimer>({ perPage: 20 });
      await Promise.all(rows.map((r) => pb.collection("active_timer").delete(r.id)));
    },
    onSuccess: invalidate,
  });

  return { logEntry, updateEntry, deleteEntry, setInvoiced, startTimer, updateTimer, clearTimer };
}
